import { ref, computed } from 'vue';

/**
 * Composable for pagination functionality
 * @param {Object} options - Configuration options
 * @param {number} options.initialPage - Initial page number (default: 1)
 * @param {number} options.initialPerPage - Initial items per page (default: 10)
 * @param {Function} options.onPageChange - Callback when page changes (optional)
 * @param {Function} options.onPerPageChange - Callback when items per page changes (optional)
 * @returns {Object} Pagination state and methods
 */
export function usePagination(options = {}) {
    const {
        initialPage = 1,
        initialPerPage = 10,
        onPageChange = null,
        onPerPageChange = null
    } = options;

    const currentPage = ref(initialPage);
    const resultsPerPage = ref(initialPerPage);
    const totalResults = ref(0);

    /**
     * Computed property for total pages
     */
    const totalPages = computed(() => {
        return Math.ceil(totalResults.value / resultsPerPage.value);
    });

    /**
     * Go to a specific page
     * @param {number} page - The page number to go to
     */
    const goToPage = (page) => {
        if (page !== currentPage.value && page >= 1 && page <= totalPages.value) {
            currentPage.value = page;
            if (onPageChange && typeof onPageChange === 'function') {
                onPageChange(page);
            }
        }
    };

    /**
     * Go to the next page
     */
    const nextPage = () => {
        if (currentPage.value < totalPages.value) {
            currentPage.value++;
            if (onPageChange && typeof onPageChange === 'function') {
                onPageChange(currentPage.value);
            }
        }
    };

    /**
     * Go to the previous page
     */
    const prevPage = () => {
        if (currentPage.value > 1) {
            currentPage.value--;
            if (onPageChange && typeof onPageChange === 'function') {
                onPageChange(currentPage.value);
            }
        }
    };

    /**
     * Change the number of results per page
     * @param {number} newLimit - The new limit for results per page
     */
    const changeResultsPerPage = (newLimit) => {
        resultsPerPage.value = newLimit;
        currentPage.value = 1; // Reset to first page
        if (onPerPageChange && typeof onPerPageChange === 'function') {
            onPerPageChange(newLimit);
        } else if (onPageChange && typeof onPageChange === 'function') {
            // If no specific per-page callback, use the page change callback
            onPageChange(1);
        }
    };

    /**
     * Reset pagination to initial values
     */
    const resetPagination = () => {
        currentPage.value = initialPage;
        resultsPerPage.value = initialPerPage;
        totalResults.value = 0;
    };

    /**
     * Set total results (used by parent component after fetching data)
     * @param {number} total - Total number of results
     */
    const setTotalResults = (total) => {
        totalResults.value = total;
    };

    return {
        // State
        currentPage,
        resultsPerPage,
        totalResults,
        totalPages,

        // Methods
        goToPage,
        nextPage,
        prevPage,
        changeResultsPerPage,
        resetPagination,
        setTotalResults
    };
}
